import React, { useEffect, useState, useRef } from 'react'
import { DualRing } from 'react-spinners-css'
import { useInView } from 'react-intersection-observer'
import { StarIcon, TrashIcon, ClipboardIcon, PlayIcon, StopIcon } from '@heroicons/react/24/solid'
import 'bootstrap/dist/css/bootstrap.min.css'
import Badge from 'react-bootstrap/Badge'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import { Star, Shuffle } from 'react-bootstrap-icons'

import SideBar from './components/Sidebar'
import LabelDropDown from './components/LabelDropDownMenu'
import { LandingPage } from './components/LandingPage'
import {
  AddLabelDiv,
  Header,
  ClickableBadge,
  BadgeLabel,
  OtherBadgeLabel,
  TextSearchBox,
  StyledDebounceInput,
  CustomButton,
  AppContainer,
  IconContainer,
  VerticalContainer,
  FilterResultCountSpan,
  Container,
  H1,
  AuthorNameH3,
  DocumentTitleH4,
  ClippedAtSpan,
  LabelsArea,
  ClippingFooter,
  A,
  AutoHidingTooltip,
  Clippings,
  QuotePWrapper,
  QuoteTextWrapper,
  QuoteP,
  IconButton,
  ResultBox,
  HeaderDiv,
  UploadDiv,
  UploadFile,
  UploadLabel,
  SettingsDiv,
  ProfileDiv,
  ProfilePic,
  Spinner,
  TtsSpinnerIcon,
  WelcomeDiv,
  WelcomeP,
  LogOutButton,
  ClipMeLogo,
  LoadingMoreClippings,
  StyledBadgeDelete
} from './styled'

import { useGoogleAuth } from './hooks/use-auth'
import { clippingsApi } from './services/clippingsApi'

const ENDPOINT = process.env.REACT_APP_API_ENDPOINT

function App() {
  const [allLabels, setAllLabels] = useState([])
  const [labelCreated, setLabelCreated] = useState(false)
  const [clips, setClips] = useState([])
  const [nResults, setNResults] = useState(-1)
  const [showLoader, setShowLoader] = useState(true)
  const [labelFilterActive, setLabelFilterActive] = useState('')
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState('')
  const limit = 20
  const [offset, setOffset] = useState(0)
  const prevFilterRef = useRef()
  const [ttsVoices, setTtsVoices] = useState([])
  const [selectedVoice, setSelectedVoice] = useState('')
  const [playingClipId, setPlayingClipId] = useState(null)
  const [loadingClipId, setLoadingClipId] = useState(null)
  const [ttsError, setTtsError] = useState('')
  const audioRef = useRef(null)

  const { signOut, isSignedIn } = useGoogleAuth()
  const { ref, inView } = useInView({
    rootMargin: '200px', threshold: 0, delay: 0
  })

  const checkFilter = (filter, count) => {
    setNResults(count >= 20 ? count + '+' : count)
  }

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const labels = await clippingsApi.fetchLabels()
        setAllLabels(labels || [])
      } catch (error) {
        console.error('failed to fetch labels:', error)
        setAllLabels([])
      }
    }

    if (isSignedIn) {
      fetchLabels()
    }
  }, [labelCreated, isSignedIn])

  useEffect(() => {
    const loadVoices = async () => {
      try {
        const voices = await clippingsApi.fetchTTSVoices()
        setTtsVoices(voices)
        if (voices.length > 0 && !selectedVoice) setSelectedVoice(voices[0].id)
      } catch (e) {
        console.error('failed to fetch TTS voices:', e)
      }
    }
    if (isSignedIn) loadVoices()
  }, [isSignedIn])

  useEffect(() => {
    const loadMoreClippings = async () => {
      try {
        const newOffset = offset + limit
        const data = await clippingsApi.fetchClippings(prevFilterRef.current, newOffset)

        if (data) {
          setClips((prev) => [...prev, ...data.clippings])
          setOffset(newOffset)
          if (data.count < limit) {
            setShowLoader(false)
          }
        }
      } catch (error) {
        console.error('failed to load more clippings:', error)
      }
    }

    if (inView) {
      loadMoreClippings()
    }
  }, [inView, offset, limit])

  useEffect(() => {
    const fetchInitialClips = async () => {
      if (!isSignedIn) return

      try {
        prevFilterRef.current = filter
        const data = await clippingsApi.fetchClippings(filter, 0)

        if (data) {
          setClips(data.clippings)
          checkFilter(filter, data.count)
          setShowLoader(data.count >= limit)
        }
      } catch (error) {
        console.error('failed to fetch initial clippings:', error)
        setClips([])
      }
    }

    fetchInitialClips()
    window.scrollTo(0, 0)
  }, [filter, isSignedIn, limit])

  const uploadFile = async (event) => {
    setLoading(true)
    const file = event.target.files[0]

    try {
      await clippingsApi.uploadFile(file)
      window.location.reload(false)
    } catch (error) {
      console.error('failed to upload file:', error)
    } finally {
      setLoading(false)
      // reset file input
      const list = new DataTransfer()
      const emptyFile = new File([], '')
      list.items.add(emptyFile)
      event.target.files = list.files
    }
  }

  const deleteClipping = async (event) => {
    const clippingId = event.target.getAttribute('data-clipping-id')

    try {
      await clippingsApi.deleteClipping(clippingId)
      setClips(clips.filter(clip => clip.ID !== clippingId))
    } catch (error) {
      console.error('failed to delete clipping:', error)
    }
  }

  const toggleClippingAsFavorite = async (event) => {
    const clippingId = event.target.getAttribute('data-clipping-id')
    const newFavoriteState = event.target.getAttribute('data-is-favorite') === 'false'

    const toggleFavorite = (id) => {
      setClips(clips.map(clip =>
        clip.ID === id ? { ...clip, IsFavorite: !clip.IsFavorite } : clip
      ))
    }

    toggleFavorite(clippingId)

    try {
      await clippingsApi.toggleFavorite(clippingId, newFavoriteState)
    } catch (error) {
      // revert on error
      toggleFavorite(clippingId)
    }
  }

  const handleLabelDelete = async (event) => {
    const clippingId = event.target.getAttribute('data-clipping-id')
    const labelName = event.target.getAttribute('data-label-name')

    const handleDeletion = (id, label) => {
      setClips(clips.map(clip => {
        if (clip.ID === id) {
          return {
            ...clip,
            Labels: clip.Labels.filter(l => l !== label)
          }
        }
        return clip
      }))
    }

    handleDeletion(clippingId, labelName)

    try {
      await clippingsApi.removeLabelFromClipping(labelName, clippingId)
    } catch (error) {
      // could add state restoration on error if needed
      console.error('failed to remove label:', error)
    }
  }

  const handleFilterChange = (filter) => {
    setShowLoader(true)
    setOffset(0)
    setClips([])
    setFilter(filter)
    setLabelFilterActive('')
  }

  const handleSearchFilterChange = (e) => {
    const filter = `search=${e.target.value}`
    handleFilterChange(filter)
  }

  const handleClickTitle = (title) => {
    const filter = `title=${title}`
    handleFilterChange(filter)
  }

  const handleClickAuthor = (author) => {
    const filter = `author=${author}`
    handleFilterChange(filter)
  }

  const resetFilters = () => {
    setFilter('')
    window.location.reload(false)
  }

  const handleDeleteAllClippings = async () => {
    if (!window.confirm('Delete ALL clippings, authors, and documents? This cannot be undone.')) {
      return
    }
    try {
      await clippingsApi.deleteAllClippings()
      setClips([])
      setNResults(0)
      setOffset(0)
      setShowLoader(false)
      const labels = await clippingsApi.fetchLabels()
      setAllLabels(labels || [])
    } catch (error) {
      console.error('failed to delete all clippings:', error)
    }
  }

  const getRandomClip = () => {
    const filter = `random=true&${Math.random()}`
    handleFilterChange(filter)
  }

  const getFavoriteClips = () => {
    const filter = 'isFavorite=true'
    handleFilterChange(filter)
  }

  const handleLabelFilter = (event) => {
    const labelName = event.target.innerHTML
    const filter = `label=${labelName}`
    handleFilterChange(filter)
    setLabelFilterActive(labelName)
  }

  const handleLabelDeleteCascade = async (event) => {
    const labelName = event.target.getAttribute('data-label-name')

    const handleDeletion = (labelName) => {
      // update clips state
      setClips(clips.map(clip => {
        if (clip.Labels.includes(labelName)) {
          return {
            ...clip,
            Labels: clip.Labels.filter(l => l !== labelName)
          }
        }
        return clip
      }))

      // update labels state
      setAllLabels(allLabels.filter(label => label.Name !== labelName))
    }

    try {
      handleDeletion(labelName)
      await clippingsApi.deleteLabelCascade(labelName)
    } catch (error) {
      console.error('failed to delete label cascade:', error)
      // could add state restoration here if needed
    }
  }

  const LabelsBar = (allLabels) => {
    return (
      <div>{allLabels.map(label => (
        <OtherBadgeLabel>
          <ClickableBadge key={label.Name} active={labelFilterActive === label.Name ? 1 : 0} onClick={handleLabelFilter} key={label.Name}>
            {label.Name}
          </ClickableBadge>
          <DeleteLabel labelName={label.Name} />
        </OtherBadgeLabel>
      ))}
      </div>
    )
  }

  const BadgeDelete = (props) => {
    return (
      <StyledBadgeDelete onClick={handleLabelDelete} data-label-name={props.labelName} data-clipping-id={props.clippingID}>
        ❌
      </StyledBadgeDelete>
    )
  }

  const DeleteLabel = (props) => {
    return (
      <StyledBadgeDelete onClick={handleLabelDeleteCascade} data-label-name={props.labelName}>
        ❌
      </StyledBadgeDelete>
    )
  }

  const LabelDropDownComponent = ({ allLabels, ID, Labels, clips, stateChanger }) => {
    return <LabelDropDown allLabels={allLabels} clippingID={ID} clipLabels={Labels} clips={clips} setClips={setClips} setLabelCreated={setLabelCreated} labelCreated={labelCreated} />
  }

  const handlePlayTTS = async (text, clipId) => {
    if (playingClipId === clipId && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setPlayingClipId(null)
      return
    }
    try {
      setTtsError('')
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setLoadingClipId(clipId)
      const voiceId = selectedVoice || ttsVoices[0]?.id || 'b0Ev8lcOOXx2o9ZcF46H'
      const blob = await clippingsApi.fetchTTS(text, voiceId)
      const url = URL.createObjectURL(blob)
      const audio = new Audio(url)
      audioRef.current = audio
      setLoadingClipId(null)
      setPlayingClipId(clipId)
      audio.onended = () => {
        URL.revokeObjectURL(url)
        audioRef.current = null
        setPlayingClipId(null)
      }
      audio.onerror = () => {
        URL.revokeObjectURL(url)
        audioRef.current = null
        setPlayingClipId(null)
      }
      await audio.play()
    } catch (err) {
      setTtsError(err.message || 'TTS failed')
      setPlayingClipId(null)
    } finally {
      setLoadingClipId(null)
    }
  }

  function Snippet(Text, ID, ClippedAt, IsFavorite, Labels, clips) {
    const renderTooltip = (props) => (
      <AutoHidingTooltip id='button-tooltip' {...props}>
        Copied to clipboard
      </AutoHidingTooltip>
    )
    return (
      <>
        <QuoteTextWrapper>
          <QuoteP>{Text}</QuoteP>
          <VerticalContainer>
            <IconContainer>
              <IconButton
                onClick={() => handlePlayTTS(Text, ID)}
                title={loadingClipId === ID ? 'Loading...' : playingClipId === ID ? 'Stop' : 'Read aloud'}
                disabled={loadingClipId === ID}
              >
                <span className='sr-only'>
                  {loadingClipId === ID ? 'Loading' : playingClipId === ID ? 'Stop' : 'Read aloud'}
                </span>
                {loadingClipId === ID ? (
                  <TtsSpinnerIcon>
                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="20 40" strokeLinecap="round" />
                  </TtsSpinnerIcon>
                ) : playingClipId === ID ? (
                  <StopIcon />
                ) : (
                  <PlayIcon />
                )}
              </IconButton>
              <OverlayTrigger
                placement='right' trigger='click'
                overlay={renderTooltip}
              >
                <IconButton onClick={() => { navigator.clipboard.writeText(Text) }}>
                  <ClipboardIcon />
                </IconButton>
              </OverlayTrigger>
              <IconButton color={IsFavorite ? 'orange' : 'lightgrey'} onClick={toggleClippingAsFavorite} data-is-favorite={IsFavorite} data-clipping-id={ID}>
                <span className='sr-only'>{IsFavorite ? 'remove from favorites' : '⭐ Mark as favorite'}</span>
                <StarIcon />
              </IconButton>
              <IconButton onClick={deleteClipping} data-clipping-id={ID}>
                <span className='sr-only'> Delete </span>
                <TrashIcon />
              </IconButton>
            </IconContainer>
            <AddLabelDiv>
              <LabelDropDownComponent stateChanger={setClips} allLabels={allLabels} clips={clips} ID={ID} Labels={Labels} />
            </AddLabelDiv>
          </VerticalContainer>
        </QuoteTextWrapper>
        <ClippingFooter>
          <ClippedAtSpan>Clipped at: {ClippedAt.slice(0, 10)}</ClippedAtSpan>
          <LabelsArea>
            {Labels.map(label => (
              <BadgeLabel>
                <Badge key={ID} size='lg' style={{ padding: '6px 10px 6px', fontSize: '0.9em' }}>
                  {label}
                  <BadgeDelete labelName={label} clippingID={ID} />
                </Badge>
              </BadgeLabel>
            ))}
          </LabelsArea>
        </ClippingFooter>
      </>
    )
  }

  return isSignedIn
    ? (
      <>
        <HeaderDiv>
          <ClipMeLogo onClick={resetFilters}>ClipMe</ClipMeLogo>
          <H1>Your clippings</H1>
          <SettingsDiv>
            <ProfileDiv>
              <ProfilePic src={localStorage.getItem('profilePic')} />
              <WelcomeDiv>
                <WelcomeP>Hello, {localStorage.getItem('name')} </WelcomeP>
              </WelcomeDiv>
            </ProfileDiv>
            <UploadDiv>
              <UploadLabel>Upload your "My Clippings.txt"</UploadLabel>
              <UploadFile type='file' onChange={uploadFile} />
              {loading ? <Spinner><DualRing color='#F2E366' size={120} /></Spinner> : null}
            </UploadDiv>
            <CustomButton variant='outline-danger' onClick={handleDeleteAllClippings}>Delete all</CustomButton>
            <LogOutButton onClick={signOut}>Log out</LogOutButton>
          </SettingsDiv>
        </HeaderDiv>

        <Container>
          <SideBar onClickTitle={handleClickTitle} onClickAuthor={handleClickAuthor} />
          <AppContainer>
            <Clippings>
              <Header>
                {ttsError && (
                  <div style={{ color: '#c00', marginBottom: 8, fontSize: '0.9em' }}>
                    {ttsError}
                  </div>
                )}
                <TextSearchBox>
                  {ttsVoices.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 12 }}>
                      <label style={{ marginRight: 6, fontSize: '0.9em' }}>TTS voice: </label>
                      <select
                        value={selectedVoice}
                        onChange={(e) => setSelectedVoice(e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 4 }}
                      >
                        {ttsVoices.map((v) => (
                          <option key={v.id} value={v.id}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <StyledDebounceInput
                    placeholder='Search anywhere'
                    minLength={2}
                    debounceTimeout={300}
                    onChange={handleSearchFilterChange}
                    value={filter.match(/search=.+/g) ? filter.match(/(search=)(.+)/)[2] : ''}
                    onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault() } }}
                  />
                  <CustomButton variant='outline-primary' onClick={resetFilters}>All</CustomButton>
                  <CustomButton onClick={getRandomClip}>
                    <Shuffle /> &nbsp;
                    Random
                  </CustomButton>
                  <CustomButton onClick={getFavoriteClips}>
                    <Star /> &nbsp;
                    Favorites
                  </CustomButton>

                </TextSearchBox>
                <FilterResultCountSpan>
                  {nResults === -1 ? '' : `${nResults} results`}
                </FilterResultCountSpan>
              </Header>

              {LabelsBar(allLabels)}
              <ResultBox>
                {clips.map(({ ID, AuthorName, DocumentTitle, Text, ClippedAt, IsFavorite, Labels }, index) => (
                  <React.Fragment key={ID}>
                    {(index === 0 || DocumentTitle !== clips[index - 1].DocumentTitle) && (
                      <>
                        <AuthorNameH3>{AuthorName}</AuthorNameH3>
                        <DocumentTitleH4>{DocumentTitle}</DocumentTitleH4>
                      </>
                    )}
                    <QuotePWrapper key={index}>
                      {Snippet(Text, ID, ClippedAt, IsFavorite, Labels, clips)}
                    </QuotePWrapper>
                  </React.Fragment>
                )
                )}
              </ResultBox>
              {clips.length >= limit && showLoader &&
                <LoadingMoreClippings ref={ref}>Loading more clippings...</LoadingMoreClippings>}
            </Clippings>

          </AppContainer>
        </Container>
      </>
    )
    : LandingPage()
};

export { ResultBox, A, H1 }
export default App
